import Application from "../../Application";
import ShowSuccessMessageInteractor from "../../core/interactor/notifications/ShowSuccessMessageInteractor";
import NameTranslation from "../../core/model/translations/NameTranslation";
import withStore, {
  TLoadingAwarePresenter,
  TPresentable
} from "../../core/presenter/withStore";
import { IIndexTypeCategory } from "../../indexTypes/model/IndexTypeCategory";
import CreateOrganisationIndexCategoryInteractor from "../interactors/CreateOrganisationIndexCategoryInteractor";
import GetAllOrganisationIndexCategoriesInteractor from "../interactors/GetAllOrganisationIndexCategoriesInteractor";
import UpdateOrganisationIndexCategoryInteractor from "../interactors/UpdateOrganisationIndexCategoryInteractor";
import { IOrganisationIndexCategory } from "../model/OrganisationIndexCategory";
import IndexTypeCategoryPresentationModel, {
  TIndexTypeCategoryPresentationModel
} from "./model/IndexTypeCategoryPresentationModel";

export interface TOrganisationIndexCategoryPresenter
  extends TLoadingAwarePresenter {
  organisationId: number;
  indexTypeCategories: IIndexTypeCategory[];
  organisationIndexCategories: TIndexTypeCategoryPresentationModel[];
  indexTypeCategoryId: number;
  createOrganisationCategoryModalVisible: boolean;
}

export interface IOrganisationIndexCategoryPresenter
  extends TOrganisationIndexCategoryPresenter,
    TPresentable {
  loadData(data: IOrganisationIndexCategory[]): void;
  loadReferenceValues(
    indexTypeCategories: IIndexTypeCategory[],
    organisationId: number
  ): void;
  createCategory(data: IOrganisationIndexCategory): Promise<boolean>;
  updateOrganisationIndexCategory(data: IOrganisationIndexCategory): void;
  showOrganisationIndexDetails(id: number): void;
  changeCreateCategoryModalVisible(
    value: boolean,
    indexTypeCategoryId: number
  ): void;
}

const defaultState: TOrganisationIndexCategoryPresenter = {
  organisationId: NaN,
  indexTypeCategories: [],
  organisationIndexCategories: [],
  indexTypeCategoryId: NaN,
  createOrganisationCategoryModalVisible: false
};

const OrganisationIndexCategoryPresenter = withStore<
  IOrganisationIndexCategoryPresenter,
  TOrganisationIndexCategoryPresenter
>(
  ({
    application,
    store,
    loader,
    translate
  }): IOrganisationIndexCategoryPresenter => {
    const _store = store;
    const _application: Application = application;
    const state = _store.getState<
      TOrganisationIndexCategoryPresenter & TLoadingAwarePresenter
    >();
    const loadReferenceValues = (
      indexTypeCategories: IIndexTypeCategory[],
      organisationId: number
    ) => {
      _store.update({ organisationId, indexTypeCategories });
    };
    loader.start("organisationIndexCategoriesLoader");
    const loadData = (data) => {
      const { indexTypeCategories } =
        _store.getState<TOrganisationIndexCategoryPresenter>();
      const { selectedLanguage } = _store.getState<any>();
      let organisationIndexCategories = (
        indexTypeCategories !== null && indexTypeCategories !== undefined
          ? indexTypeCategories
          : []
      ).map((item) =>
        IndexTypeCategoryPresentationModel(item, data, selectedLanguage)
      );
      loader.stop("organisationIndexCategoriesLoader");
      _store.update({ organisationIndexCategories });
    };
    const getAllOrganisationIndexCategories = async () => {
      const { organisationId } =
        _store.getState<TOrganisationIndexCategoryPresenter>();
      let categories = await _application.container
        .resolve<GetAllOrganisationIndexCategoriesInteractor>(
          "getAllOrganisationIndexCategories"
        )
        .execute(organisationId);
      loadData(categories);
    };
    const createCategory = async (data: any) => {
      loader.start("organisationIndexCategoriesLoader");

      const { selectedLanguage } = _store.getState<any>();
      let translations = data.nameTranslations.map((item) => {
        var element = NameTranslation(item);
        element.organisationId = data.organisationId;
        return element;
      });
      let originalName = translations?.find(
        (item) => item.languageId === selectedLanguage.id
      );
      originalName = originalName ? originalName : translations[0];
      data.name = originalName?.nameTranslation ?? "";
      data.nameTranslations = translations;
      await _application.container
        .resolve<CreateOrganisationIndexCategoryInteractor>(
          "createOrganisationIndexCategory"
        )
        .execute(data);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.project.create_success_message"));
      loader.stop("organisationIndexCategoriesLoader");
      await getAllOrganisationIndexCategories();
      return true;
    };

    const updateOrganisationIndexCategory = async (
      data: IOrganisationIndexCategory
    ) => {
      loader.start("organisationIndexCategoriesLoader");
      const { selectedLanguage } = _store.getState<any>();
      let translations = data.nameTranslations.map((item) => {
        var element = NameTranslation(item);
        element.organisationId = data.organisationId;
        return element;
      });
      let originalName = translations?.find(
        (item) => item.languageId === selectedLanguage.id
      );
      data.name = originalName?.nameTranslation ?? "";
      data.nameTranslations = translations;

      await _application.container
        .resolve<UpdateOrganisationIndexCategoryInteractor>(
          "updateOrganisationIndexCategory"
        )
        .execute(data);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.beneficiary.update_success_message"));
      loader.stop("organisationIndexCategoriesLoader");
      await getAllOrganisationIndexCategories();
    };

    const showOrganisationIndexDetails = (id: number) => {
      const { organisationId } =
        _store.getState<TOrganisationIndexCategoryPresenter>();
      return _application.navigator.push({
        pathname: `/organisations/${organisationId}/categories/${id}`
      });
    };

    const changeCreateCategoryModalVisible = (
      value: boolean,
      indexTypeCategoryId: number
    ) => {
      _store.update({
        indexTypeCategoryId,
        createOrganisationCategoryModalVisible: value
      });
    };
    return {
      ...state,
      store: _store,
      loader,
      application: _application,
      translate,
      loadData,
      loadReferenceValues,
      createCategory,
      updateOrganisationIndexCategory,
      showOrganisationIndexDetails,
      changeCreateCategoryModalVisible
    };
  },
  defaultState
);

export default OrganisationIndexCategoryPresenter;
